import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  async login(email, password) {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  async register(name, email, password) {
    const response = await api.post('/auth/register', { name, email, password });
    return response.data;
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Songs API
export const songsAPI = {
  async getSongs() {
    const response = await api.get('/songs');
    return response.data;
  },

  async getSong(songId) {
    const response = await api.get(`/songs/${songId}`);
    return response.data;
  },

  async createSong(formData) {
    const response = await api.post('/songs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateSong(songId, updates) {
    const response = await api.put(`/songs/${songId}`, updates);
    return response.data;
  },

  async deleteSong(songId) {
    const response = await api.delete(`/songs/${songId}`);
    return response.data;
  },

  async likeSong(songId) {
    const response = await api.post(`/songs/${songId}/like`);
    return response.data;
  },

  async unlikeSong(songId) {
    const response = await api.delete(`/songs/${songId}/like`);
    return response.data;
  },

  async getLikedSongs() {
    const response = await api.get('/songs/liked');
    return response.data;
  },

  async trackPlay(songId) {
    const response = await api.post(`/songs/${songId}/play`);
    return response.data;
  }
};

// Vibes API
export const vibesAPI = {
  async getVibes() {
    const response = await api.get('/vibes');
    return response.data;
  },

  async getVibe(vibeId) {
    const response = await api.get(`/vibes/${vibeId}`);
    return response.data;
  },

  async getVibeSongs(vibeId) {
    const response = await api.get(`/vibes/${vibeId}/songs`);
    return response.data;
  },

  async createVibe(vibeData) {
    const response = await api.post('/vibes', vibeData);
    return response.data;
  },

  async updateVibe(vibeId, updates) {
    const response = await api.put(`/vibes/${vibeId}`, updates);
    return response.data;
  },

  async deleteVibe(vibeId) {
    const response = await api.delete(`/vibes/${vibeId}`);
    return response.data;
  }
};

// Config API
export const configAPI = {
  async getStatus() {
    const response = await api.get('/config/status');
    return response.data;
  },

  async configureBunny(storageZoneName, accessKey, cdnHostname) {
    const formData = new FormData();
    formData.append('storage_zone_name', storageZoneName);
    formData.append('access_key', accessKey);
    formData.append('cdn_hostname', cdnHostname);
    
    const response = await api.post('/config/bunny', formData);
    return response.data;
  }
};

// Bunny API
export const bunnyAPI = {
  async listFiles() {
    const response = await api.get('/bunny/files');
    return response.data;
  },

  async importSongs(importData) {
    const response = await api.post('/bunny/import', importData);
    return response.data;
  }
};

export default api;